import { useCallback } from 'react';
import { Box } from '@mui/material';

import { useUser } from '../../contexts';
import { setSessionUser } from '../../contexts/user/user.action';
import {
    getCachedUserLocations,
    setCachedUserLocations,
    storeToken,
    showErrorAlert,
} from '../../utilities';
import {
    fetchMyLocations,
    switchMyLocation,
} from '../../services/e-consent/user-locations.services';
import { User, UserLocationOption } from '../../services/types';
import LazyAutocomplete from '../Inputs/LazyAutocomplete';
import { Field } from '../Inputs/types';

const LOCATION_FIELD: Field = {
    name: 'location',
    label: 'Location',
    type: 'autocomplete',
    additional: null,
};

function userLocationToOption(o: UserLocationOption) {
    return {
        value: o.code,
        description: o.description ? `${o.description} (${o.code})` : o.code,
    };
}

export type LocationAutocompleteProps = {
    onCloseMenu: () => void;
};

function LocationAutocomplete({ onCloseMenu }: LocationAutocompleteProps) {
    const {
        state: { user },
        dispatch,
    } = useUser();

    const fetchOptions = useCallback(async () => {
        const cached = getCachedUserLocations();
        if (cached !== null) {
            return cached.map(userLocationToOption);
        }
        try {
            const data = await fetchMyLocations();
            setCachedUserLocations(data);
            return data.map(userLocationToOption);
        } catch {
            const again = getCachedUserLocations();
            if (again !== null) {
                return again.map(userLocationToOption);
            }
            await showErrorAlert({
                title: 'Locations',
                text: 'Could not load your locations.',
            });
            return [];
        }
    }, []);

    const handleChange = useCallback(
        async (_name: string, code: string) => {
            if (!user) return;
            if (code === (user.locationCode ?? '')) return;

            const cached = getCachedUserLocations() ?? [];
            const selected = code
                ? cached.find((o) => o.code === code) ?? null
                : null;

            try {
                const session = await switchMyLocation(selected?.code);
                storeToken(session.token, session.refreshToken, session.expiryInSecond);
                const newUser: User = {
                    ...user,
                    locationCode: undefined,
                    locationDescription: undefined,
                };

                if (selected) {
                    newUser.locationCode = selected.code;
                    newUser.locationDescription = selected.description;
                }
                dispatch(setSessionUser(newUser));
                onCloseMenu();
            } catch {
                await showErrorAlert({
                    title: 'Switch location',
                    text: 'Could not switch location. Please try again.',
                });
            }
        },
        [user, dispatch, onCloseMenu]
    );

    if (!user) {
        return null;
    }

    return (
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
            <LazyAutocomplete
                field={LOCATION_FIELD}
                value={user.locationCode ?? ''}
                isRequired={false}
                fetchOptions={fetchOptions}
                onChange={handleChange}
                disablePortal={false}
            />
        </Box>
    );
}

export default LocationAutocomplete;
